import { useState, useCallback, useRef, useEffect } from 'react';
import type { InvoiceCreatedPayload } from '@techgitter/contracts';
import { createInvoice, type CreateInvoiceParams } from '../services/invoicesApi';
import { onSignalREvent } from '../services/signalRService';

export type InvoiceCreationPhase = 'idle' | 'submitting' | 'accepted' | 'succeeded' | 'failed';

const TIMEOUT_MS = 30_000;

interface UseInvoiceCreationResult {
  phase: InvoiceCreationPhase;
  invoiceNumber: string | null;
  invoiceId: string | null;
  error: string | null;
  submit: (params: CreateInvoiceParams) => Promise<void>;
  reset: () => void;
}

export function useInvoiceCreation(): UseInvoiceCreationResult {
  const [phase, setPhase] = useState<InvoiceCreationPhase>('idle');
  const [invoiceNumber, setInvoiceNumber] = useState<string | null>(null);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resolvedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const signalRUnsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      signalRUnsubRef.current?.();
    };
  }, []);

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    signalRUnsubRef.current?.();
    signalRUnsubRef.current = null;
  }, []);

  const startSignalRListener = useCallback(() => {
    const unsubscribe = onSignalREvent('invoices:created', (data: unknown) => {
      if (resolvedRef.current) return;
      const payload = data as InvoiceCreatedPayload | undefined;
      if (!payload?.number) return;

      resolvedRef.current = true;
      cleanup();
      setInvoiceNumber(payload.number);
      setInvoiceId(payload.invoiceId);
      setPhase('succeeded');
    });
    signalRUnsubRef.current = unsubscribe;
  }, [cleanup]);

  const submit = useCallback(async (params: CreateInvoiceParams) => {
    setPhase('submitting');
    setError(null);
    setInvoiceNumber(null);
    setInvoiceId(null);
    resolvedRef.current = false;

    // Register SignalR listener before POST
    startSignalRListener();

    try {
      await createInvoice(params);
      setPhase('accepted');

      // Fallback timeout
      timeoutRef.current = setTimeout(() => {
        if (!resolvedRef.current) {
          resolvedRef.current = true;
          cleanup();
          setError('Invoice creation timed out. The invoice may still be created — check the Invoices page.');
          setPhase('failed');
        }
      }, TIMEOUT_MS);
    } catch (err) {
      cleanup();
      setError(err instanceof Error ? err.message : 'Failed to create invoice');
      setPhase('failed');
    }
  }, [startSignalRListener, cleanup]);

  const reset = useCallback(() => {
    resolvedRef.current = true;
    cleanup();
    setPhase('idle');
    setInvoiceNumber(null);
    setInvoiceId(null);
    setError(null);
  }, [cleanup]);

  return {
    phase,
    invoiceNumber,
    invoiceId,
    error,
    submit,
    reset,
  };
}
